import { ButtonAPI, FieldAPI, FormAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnChange, OnClick, OnClose, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { Inject } from '@odinjs/odin';
import BaseController from '../BaseController';
import { CONFIRMATION_FIELD_VALID, confirmationFieldValidator, NEW_PWD_VALIDATOR, newPwdValidator, optionsValidator } from '../../utils/user-util';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Value } from '@philips-internal/oswald-ui-core';
import { AuthenticationService, HttpService, JWTToken } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import TokenConstants from '../../constants/TokenConstants';
import { Message, ToastResponse } from '../../interfaces/ToastResponse';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { forEachMessageShowToast } from '../../services/toast-notication-utils';
import { ChangePasswordAliases, FragmentNames } from '../../codegen';

@Controller({ fragment: FragmentNames.CHANGE_PASSWORD, component: ChangePasswordAliases.ROOT_MODAL })
class ChangePasswordController extends BaseController {

  @Inject(ChangePasswordAliases.OLD_PASSWORD_FIELD)
  fieldActualPassword: FieldAPI;

  @Inject(ChangePasswordAliases.NEW_PASSWORD_FIELD)
  fieldNewPassword: FieldAPI;

  @Inject(ChangePasswordAliases.CONFIRM_PASSWORD_FIELD)
  fieldNewConfirmPassword: FieldAPI;

  @Inject(ChangePasswordAliases.RESET_FORM)
  formPassword: FormAPI;

  @Inject(ChangePasswordAliases.PASSWORD_SUBMIT_BUTTON)
  confirmBtn: ButtonAPI;

  @OnMount({ target: ChangePasswordAliases.ROOT_MODAL })
  onMountModalAltSenha(): void {
    this.fieldNewConfirmPassword.addLocalValidator(CONFIRMATION_FIELD_VALID, (value) => {
      const confirmationValue = this.formPassword.getRecord()['newPassword'] || '';
      return confirmationFieldValidator(value, confirmationValue, 'pwd');
    }, optionsValidator());
    this.fieldNewPassword.addLocalValidator(NEW_PWD_VALIDATOR, (value) => {
      const senhaAtual = this.formPassword.getRecord()['password'] || '';
      return newPwdValidator(value, senhaAtual);
    });
    this.confirmBtn.setEnabled(false);
  }

  @OnChange({ target: ChangePasswordAliases.RESET_FORM })
  async onChangeFormAltSenha({ value }: ChangeEvent<Record<string, Value>>): Promise<void> {
    this.confirmBtn.setEnabled(await this.isDataValid(value));
  }

  @OnClick({ target: ChangePasswordAliases.PASSWORD_SUBMIT_BUTTON })
  onClickAltSenha(): void {
    this.confirmBtn.setEnabled(false);
    this.formPassword.setEditing(false);
    this.alterPassword();
  }

  @OnClose({ target: ChangePasswordAliases.ROOT_MODAL })
  onRootModalClose(): void {
    if(this.isTokenEmpty()){
      this.historyService.push(APPLICATION_ROUTES.LOGIN);
    } else {
      this.historyService.push(APPLICATION_ROUTES.PROFILE);
    }
  }

  async isDataValid(value: Record<string, Value>): Promise<boolean> {
    const validation : boolean[] = await Promise.all([this.fieldNewPassword.validate(),
       this.fieldActualPassword.validate(),
       this.fieldNewConfirmPassword.validate()]);
    const isValid : boolean = validation.reduce((a,b) => a && b, true);
    return isValid &&
      Boolean(value['password']) &&
      Boolean(value['newPassword']) &&
      Boolean(value['confirmPassword']) &&
      (value['newPassword'] === value['confirmPassword']);
  }

  alterPassword(): void {
    const body = this.formPassword.getRecord();
    body.login = this.context.get('username');
    HttpService.post(APPLICATION_ENDPOINTS.POST_ALTER_PASSWORD, {}, { body }).then(response => {
      this.handleSuccess(response);
    }).catch(response => {
      this.handleFailure(response);
    });
  }

  handleSuccess(response: HttpResponse<unknown>): void {
    const record = response.body as Message;
    if (record?.messages) {
      const toastResponse = response.body as ToastResponse;
      forEachMessageShowToast(toastResponse);
      this.closeChangePasswordModal();
    }
  }

  closeChangePasswordModal(): void {
    if (this.isTokenEmpty()) {
      this.historyService.push(APPLICATION_ROUTES.LOGIN);
    } else {
      const token = AuthenticationService.getJWTToken() as JWTToken;
      const patientId: string = token.claims[TokenConstants.PATIENT_ID] as string;
      const params = { patientId };
      this.historyService.push(APPLICATION_ROUTES.PROFILE, params);
    }
  }

  isTokenEmpty(): boolean {
    return AuthenticationService.getJWTToken() == null;
  }

  async handleFailure(response: HttpResponse<unknown>): Promise<void> {
    this.formPassword.setEditing(true);
    this.confirmBtn.setEnabled(await this.isDataValid(this.formPassword.getRecord()));
    const toastResponse = response.body as ToastResponse;
    forEachMessageShowToast(toastResponse);
  }
}

export default ChangePasswordController;
